import type { Match } from '@/data/types';

/** Геометрия раскладки сетки. Все размеры в пикселях. */
// Высота карточки матча: шапка (~29px) + две строки игроков (min-height 36px каждая).
export const BRACKET_CARD_H = 104;
export const BRACKET_COL_W = 248;
export const BRACKET_COL_GAP = 64;
export const BRACKET_HEADER_H = 32;
const ROUND_GAP = 24; // вертикальный зазор между матчами первого раунда

export interface PositionedMatch {
  match: Match;
  left: number;
  top: number;
}

export interface BracketLayout {
  positioned: PositionedMatch[];
  width: number;
  height: number;
  /** Список номеров раундов по возрастанию (для подписей колонок). */
  rounds: number[];
}

/**
 * Универсальная раскладка турнирной сетки по связям `nextMatchId`.
 *
 * Алгоритм «вес поддерева»: вес матча = число листовых матчей, которые в него
 * в итоге сливаются. Каждому матчу выделяется вертикальный диапазон,
 * пропорциональный весу, и он центрируется в нём. Питатели делят диапазон
 * родителя по своему весу. Это даёт равномерную сетку и для дерева Single
 * Elim, и для нижней сетки Double Elim с её «приёмными» 1-к-1 раундами.
 *
 * `matches` — матчи одной сетки (уже отфильтрованы по stage).
 */
export function computeBracketLayout(matches: Match[]): BracketLayout {
  const rounds = Array.from(new Set(matches.map((m) => m.round))).sort((a, b) => a - b);
  if (rounds.length === 0) {
    return { positioned: [], width: 0, height: 0, rounds: [] };
  }

  const roundIndex = new Map<number, number>();
  rounds.forEach((r, i) => roundIndex.set(r, i));

  const idsInBracket = new Set(matches.map((m) => m.id));

  // Питатели каждого матча (матчи, чей nextMatchId указывает на него).
  const feedersByMatchId = new Map<string, Match[]>();
  for (const m of matches) {
    if (m.nextMatchId && idsInBracket.has(m.nextMatchId)) {
      const list = feedersByMatchId.get(m.nextMatchId) ?? [];
      list.push(m);
      feedersByMatchId.set(m.nextMatchId, list);
    }
  }
  // Питатели сортируем по slot — стабильный порядок сверху вниз.
  for (const list of feedersByMatchId.values()) {
    list.sort((a, b) => a.slot - b.slot);
  }

  // Вес поддерева: лист = 1, иначе сумма весов питателей.
  const weightCache = new Map<string, number>();
  const weightOf = (m: Match): number => {
    const cached = weightCache.get(m.id);
    if (cached !== undefined) return cached;
    const feeders = feedersByMatchId.get(m.id) ?? [];
    const w = feeders.length === 0 ? 1 : feeders.reduce((s, f) => s + weightOf(f), 0);
    weightCache.set(m.id, w);
    return w;
  };

  // Корни — матчи без nextMatchId внутри сетки (последний раунд сетки).
  const roots = matches
    .filter((m) => !m.nextMatchId || !idsInBracket.has(m.nextMatchId))
    .sort((a, b) => a.slot - b.slot);

  const step = BRACKET_CARD_H + ROUND_GAP; // высота вертикального слота одного листа
  const centerById = new Map<string, number>();

  // Рекурсивно раскладываем поддерево матча в вертикальном диапазоне
  // [rangeTop, rangeTop + weight*step]. Центр матча — середина диапазона.
  const place = (m: Match, rangeTop: number) => {
    const w = weightOf(m);
    centerById.set(m.id, rangeTop + (w * step) / 2);
    const feeders = feedersByMatchId.get(m.id) ?? [];
    let cursor = rangeTop;
    for (const f of feeders) {
      const fw = weightOf(f);
      place(f, cursor);
      cursor += fw * step;
    }
  };

  let cursor = 0;
  for (const root of roots) {
    place(root, cursor);
    cursor += weightOf(root) * step;
  }

  const positioned: PositionedMatch[] = [];
  for (const m of matches) {
    const colIdx = roundIndex.get(m.round) ?? 0;
    const left = colIdx * (BRACKET_COL_W + BRACKET_COL_GAP);
    const center = centerById.get(m.id) ?? BRACKET_CARD_H / 2;
    positioned.push({
      match: m,
      left,
      top: BRACKET_HEADER_H + center - BRACKET_CARD_H / 2,
    });
  }

  const allCenters = [...centerById.values()];
  const maxCenter = allCenters.length > 0 ? Math.max(...allCenters) : BRACKET_CARD_H / 2;
  const width = rounds.length * BRACKET_COL_W + (rounds.length - 1) * BRACKET_COL_GAP;
  const height = BRACKET_HEADER_H + maxCenter + BRACKET_CARD_H / 2;

  return { positioned, width, height, rounds };
}
